import { notification } from 'antd';
import axios, { AxiosError, AxiosResponse } from 'axios';
import qs from 'qs';
import { API_ENDPOINTS } from 'common/constants/api-endpoints.constants';
import { APIError, captureError } from 'common/utils/error.util';
import { EAuthFlagName } from 'domains/auth/constants/auth.constants';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants';
import routerTree from 'navigation/routerTree';
import i18n from '../../translations/i18n';
import { getFromLocalstorage, removeFromLocalstorage } from '../utils/local-storage.utils';

export const apiClient = axios.create({
  baseURL: API_ENDPOINTS.BASE_URL,
  withCredentials: true,
  paramsSerializer: {
    serialize: (params) => qs.stringify(params, { indices: false }),
  },
});

const apiClientInterceptorsReq = async (config: any): Promise<any> => ({
  ...config,
});

const apiClientInterceptorsReqError = async (error: AxiosError): Promise<never> => Promise.reject(error);

const apiClientInterceptorsRes = async (response: AxiosResponse): Promise<AxiosResponse<any, any>> => response;

const apiClientInterceptorsResError = async (error: AxiosError<any>): Promise<void> => {
  const { response } = error;
  const isAuth = getFromLocalstorage(EAuthFlagName);
  const searchParams = new URLSearchParams();

  // Need to find a way how to run guards before loaders run.
  if (!isAuth) {
    searchParams.append('from', window.location.pathname);
    await routerTree.navigate(`${ROUTES_MAPPING.PUBLIC.LOGIN}?${searchParams.toString()}`);

    return Promise.resolve();
  }

  if (response?.status === 401) {
    searchParams.append('from', window.location.pathname);

    notification.warning({
      message: i18n.t('apiClient.error.401.message'),
      duration: 10,
    });

    removeFromLocalstorage(EAuthFlagName);

    await routerTree.navigate(`${ROUTES_MAPPING.PUBLIC.LOGIN}?${searchParams.toString()}`);

    return Promise.reject(error);
  }

  if (response?.status === 403) {
    notification.error({
      message: i18n.t('apiClient.error.403.message'),
      description: i18n.t('apiClient.error.403.description'),
      duration: 10,
    });

    return Promise.reject(error);
  }

  if (response?.status === 500) {
    captureError(new APIError(error, response?.config.url, response?.config.method, response?.config.params));

    notification.error({
      message: i18n.t('apiClient.error.500.message'),
      description: i18n.t('apiClient.error.500.description'),
      duration: 10,
    });

    return Promise.reject(error);
  }

  notification.error({
    message: i18n.t('apiClient.error.main.message'),
    description: error?.message,
    duration: 10,
  });

  return Promise.reject(error);
};

apiClient.interceptors.request.use(apiClientInterceptorsReq, apiClientInterceptorsReqError);
apiClient.interceptors.response.use(apiClientInterceptorsRes, apiClientInterceptorsResError);
